export interface CurrencyConfig {
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number;
  exchangeRate: number;
}

export const SUPPORTED_CURRENCIES: CurrencyConfig[] = [
  {
    code: 'usd',
    name: 'US Dollar',
    symbol: '$',
    decimalPlaces: 2,
    exchangeRate: 1,
  },
  {
    code: 'eur',
    name: 'Euro',
    symbol: '€',
    decimalPlaces: 2,
    exchangeRate: 1.14935,
  },
  {
    code: 'gbp',
    name: 'British Pound',
    symbol: '£',
    decimalPlaces: 2,
    exchangeRate: 1.30628
  },
  {
    code: 'cad',
    name: 'Canadian Dollar',
    symbol: 'CA$',
    decimalPlaces: 2,
    exchangeRate: 0.707794
  },
  {
    code: 'aud',
    name: 'Australian Dollar',
    symbol: 'A$',
    decimalPlaces: 2,
    exchangeRate: 0.649218,
  },
  {
    code: 'jpy',
    name: 'Japanese Yen',
    symbol: '¥',
    decimalPlaces: 0,
    exchangeRate: 0.00634373,
  },
  {
    code: 'chf',
    name: 'Swiss Franc',
    symbol: 'CHF',
    decimalPlaces: 2,
    exchangeRate: 1.23124,
  },
  {
    code: 'nzd',
    name: 'New Zealand Dollar',
    symbol: 'NZ$',
    decimalPlaces: 2,
    exchangeRate: 0.567091,
  },
  {
    code: 'sgd',
    name: 'Singapore Dollar',
    symbol: 'S$',
    decimalPlaces: 2,
    exchangeRate: 0.762717,
  },
  {
    code: 'hkd',
    name: 'Hong Kong Dollar',
    symbol: 'HK$',
    decimalPlaces: 2,
    exchangeRate: 0.127054,
  },
  {
    code: 'sek',
    name: 'Swedish Krona',
    symbol: 'SEK',
    decimalPlaces: 2,
    exchangeRate: 0.104879,
  },
  {
    code: 'dkk',
    name: 'Danish Krone',
    symbol: 'DKK',
    decimalPlaces: 2,
    exchangeRate: 0.153879,
  },
  {
    code: 'nok',
    name: 'Norwegian Krone',
    symbol: 'NOK',
    decimalPlaces: 2,
    exchangeRate: 0.0975313,
  },
  {
    code: 'mxn',
    name: 'Mexican Peso',
    symbol: 'MX$',
    decimalPlaces: 2,
    exchangeRate: 0.054066,
  },
  {
    code: 'krw',
    name: 'South Korean Won',
    symbol: '₩',
    decimalPlaces: 0,
    exchangeRate: 0.000673191,
  },
];

/**
 * Get currency configuration by currency code
 */
export function getCurrencyConfig(currencyCode: string): CurrencyConfig | undefined {
  return SUPPORTED_CURRENCIES.find((c) => c.code === currencyCode?.toLowerCase());
}

/**
 * Validate if a currency code is supported
 */
export function isValidCurrency(code: string): boolean {
  return SUPPORTED_CURRENCIES.some(c => c.code === code.toLowerCase());
}

/**
 * Format amount based on currency decimal places
 * For Stripe API, amounts need to be in the smallest currency unit
 */
export function formatAmountForStripe(amount: number, currencyCode: string): number {
  const config = getCurrencyConfig(currencyCode);
  if (!config) {
    // Default to 2 decimal places if currency not found
    return Math.round(amount * 100);
  }

  if (config.decimalPlaces === 0) {
    // Zero-decimal currencies (JPY, KRW) - use amount as-is
    return Math.round(amount);
  }

  // Standard decimal currencies - multiply by 10^decimalPlaces
  return Math.round(amount * Math.pow(10, config.decimalPlaces));
}

/**
 * Format display amount with currency symbol
 */
export function formatCurrencyDisplay(amount: number, currencyCode: string): string {
  const config = getCurrencyConfig(currencyCode);
  if (!config) {
    return `${amount}`;
  }

  const formattedAmount = config.decimalPlaces === 0
    ? Math.round(amount).toString()
    : amount.toFixed(config.decimalPlaces);

  return `${config.symbol}${formattedAmount}`;
}
